import React from "react";
import { Platform, Text, View } from "react-native";
import { useTheme } from "../../../theme";

// Tooltip is portaled to document.body (outside #root), so the global dark-mode
// stylesheet doesn't reach it and its inline light colours are never swept.
const KEEP_COLOR_PROPS = { dataSet: { lhKeepColor: "1" } } as any;

interface MergedTicketsTooltipProps {
  /** The ticket ID that has merged tickets */
  ticketId: string;
  /** Array of absorbed ticket IDs (for backward compatibility) */
  absorbedTicketIds: string[];
  /** Details of merged tickets (ticket number + title) */
  mergedTicketsDetails?: Array<{ ticketNumber: string; title: string }>;
  /** Total count of merged tickets */
  mergedCount: number;
}

export const MergedTicketsTooltip: React.FC<MergedTicketsTooltipProps> = ({
  ticketId,
  absorbedTicketIds,
  mergedTicketsDetails = [],
  mergedCount,
}) => {
  const isDark = useTheme().resolvedTheme === "dark";
  // Use mergedTicketsDetails if available, otherwise fall back to absorbedTicketIds
  const displayItems = mergedTicketsDetails && mergedTicketsDetails.length > 0
    ? mergedTicketsDetails.slice(0, 3)
    : absorbedTicketIds.slice(0, 3);
  const hasMore = (mergedTicketsDetails?.length ?? 0) > 3;

  const tooltipContainerStyle: any = Platform.OS === "web"
    ? {
        padding: "6px 8px",
        backgroundColor: isDark ? "#2B2B2B" : "#ffffff",
        borderRadius: "6px",
        border: isDark ? "1px solid #4A4A4A" : "1px solid #d1d5db",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        width: "360px",
        maxWidth: "100%",
        boxSizing: "border-box",
        fontSize: "12px",
        color: isDark ? "#FFFFFF" : "#3a3a3a",
        lineHeight: "1.4",
      }
    : {
        paddingHorizontal: 12,
        paddingVertical: 8,
        backgroundColor: "#ffffff",
        borderRadius: 4,
      };

  const titleStyle: any = Platform.OS === "web"
    ? {
        fontWeight: "600",
        marginBottom: "6px",
        fontSize: "12px",
        ...(isDark ? { color: "#FFFFFF" } : {}),
      }
    : {
        fontWeight: "600",
        marginBottom: 6,
        fontSize: 12,
      };

  const ticketListStyle: any = Platform.OS === "web"
    ? {
        marginTop: "6px",
        borderTop: isDark ? "1px solid #4A4A4A" : "1px solid #e5e7eb",
        paddingTop: "6px",
      }
    : {
        marginTop: 6,
        paddingTop: 6,
        borderTopWidth: 1,
        borderTopColor: "#e5e7eb",
      };

  const linkColor = isDark ? "#6AADFF" : "#005CBC";

  const ticketItemStyle: any = Platform.OS === "web"
    ? {
        marginBottom: "4px",
        fontSize: "12px",
        color: linkColor,
        lineHeight: "1.4",
        fontWeight: "600",
        whiteSpace: "normal",
        wordBreak: "break-word",
        display: "block",
      }
    : {
        marginBottom: 4,
        fontSize: 12,
        color: "#005CBC",
        lineHeight: 16,
        fontWeight: "600",
        flexWrap: "wrap",
      };

  const viewAllStyle: any = Platform.OS === "web"
    ? {
        marginTop: "8px",
        color: linkColor,
        fontWeight: "600",
        cursor: "pointer",
        textDecoration: "underline",
        fontSize: "12px",
      }
    : {
        marginTop: 8,
        color: "#005CBC",
        fontWeight: "600",
        fontSize: 12,
      };

  return (
    <View style={tooltipContainerStyle}>
      {/* Header: Total count */}
      <Text style={titleStyle} {...(isDark ? KEEP_COLOR_PROPS : {})}>
        {mergedCount} merged ticket{mergedCount !== 1 ? "s" : ""}
      </Text>

      {/* Merged tickets list */}
      {displayItems.length > 0 && (
        <View style={ticketListStyle}>
          {displayItems.map((item, index) => {
            const displayText = typeof item === "string"
              ? item // Fallback: absorbedTicketIds (just ticket ID)
              : `${item.ticketNumber} - ${item.title}`; // mergedTicketsDetails format
            return (
              <View key={index} style={{ marginBottom: 4, width: "100%", flexShrink: 1 }}>
                <Text style={ticketItemStyle} {...(isDark ? KEEP_COLOR_PROPS : {})}>{displayText}</Text>
              </View>
            );
          })}
        </View>
      )}

      {/* "View All" link if more than 3 */}
      {hasMore && (
        <Text style={viewAllStyle} {...(isDark ? KEEP_COLOR_PROPS : {})}>
          View All ({mergedCount})
        </Text>
      )}
    </View>
  );
};
